import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useRef, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Button, Col, Row } from "reactstrap";
import { RootStoreContext } from "../app/stores/rootStore";
import Loader from "../components/common/Loader";
import TreeComponent from "../components/widgets/TreeComponent";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import classnames from "classnames";
import moment from "moment";
import "moment/locale/it";
import LanguageDropdown from "../components/dropdowns/LanguageDropdown";
import { DownloadZipButton } from "../components/buttons/DownloadZipButton";
import { translationHookOptions } from "../app/localization/localization";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import GenericModal from "../components/common/GenericModal";
import CaboloRecordingMeetingComponent from "../components/cabolo/CaboloRecordingMeetingComponent";

interface IParams {
  meetingId: string;
}

const MeetingsTreePage: React.FC<RouteComponentProps<IParams>> = ({
  match,
  history,
}) => {
  const rootStore = useContext(RootStoreContext);
  const { loadingNodes, loadNodes, currentTree, resetNodeTree, setSourceId } =
    rootStore.meetingStore;
  const { getMeeting, attendingMeeting, canViewSurveys } =
    rootStore.playerStore;
  const { currentTenant } = rootStore.tenantStore;
  const { selectedLanguageId, setSelectedLanguageId, theme, isNativeApp } =
    rootStore.commonStore;
  const { setSurveyParameters, resetSurveyParameters } = rootStore.surveyStore;
  const [meetingId, setMeetingId] = useState(0);
  const [meetingDate, setMeetingDate] = useState("");
  const[isOpenModalRegistrations, setIsOpenModalRegistrations] = useState(false);
  const link = useRef<HTMLAnchorElement | null>(null);
  const [t] = useTranslation(
    ["translation", "features", "cabolo"],
    translationHookOptions
  );
  const sourceType = "meetings";
  const { isMobileMediaQuery } = rootStore.responsiveStore;

  const handleActionDownload = (filename : string, href : string ) => {
    // console.log("Download file: ", filename, " from: ", href);
    link.current!.download = filename;
    link.current!.href = href;
    link.current!.click();
  }


  useEffect(() => {
    setSourceId(parseInt(match.params.meetingId));

    let intMeetingId = parseInt(match.params.meetingId);
    if (intMeetingId) {
      setMeetingId(intMeetingId);
      if (canViewSurveys) setSurveyParameters(intMeetingId);
      let meet = getMeeting(intMeetingId);
      if (meet) {
        let momentStart = moment(meet.startDate);
        let stringDate = t("meeting_page_date", {
          val: momentStart.toDate(),
          formatParams: {
            val: {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            },
          },
        });
        setMeetingDate(stringDate);
      }
      loadNodes(intMeetingId, selectedLanguageId)
        .then((res) => {
          const scrollableElement = document.querySelector(".odgPage");
          if (scrollableElement) disableBodyScroll(scrollableElement);
        })
        .catch();
    } else history.push("../meetings");

    return () => {
      resetSurveyParameters();
      const scrollableElement = document.querySelector(".odgPage");
      if (scrollableElement) enableBodyScroll(scrollableElement);
      resetNodeTree();
    };
  }, [
    loadNodes,
    history,
    match.params.meetingId,
    resetNodeTree,
    getMeeting,
    canViewSurveys,
    setSurveyParameters,
    resetSurveyParameters,
    selectedLanguageId,
    setSourceId,
    t,
  ]);

  if (loadingNodes) return <Loader />;

  return (
    <>
        <a role="button" href="/#" ref={link} style={{display: "none"}} > </a>
    <div className="odgPage">
      <Row>
        {attendingMeeting?.id !== meetingId ? (
          <div
            className={classnames(
              isMobileMediaQuery ? "odgTitoloMobile" : "odgTitolo",
              `${theme!.titleBarBgOdg} ${theme!.titleBarTextOdg}`
            )}
          >
            {currentTree
              ? currentTree.title
              : t("meeting_page_generic_title", { ns: "features" })}
          </div>
        ) : (
          <div
            className={classnames(
              isMobileMediaQuery ? "odgTitoloMobile" : "odgTitolo",
              `${theme!.titleBarBgOdg} ${theme!.titleBarTextOdg}`
            )}
          >
            {t("meeting_page_title_bar", { ns: "features" })}
          </div>
        )}
      </Row>
      <Row>
        <div
          className={classnames(
            isMobileMediaQuery ? "odgSottoTitoloMobile" : "odgSottoTitolo",
            `${theme!.colorTextDateOdg}`
          )}
        >
          {meetingDate}
          <div className="float-right mr-3">
            {currentTree && !isMobileMediaQuery && (
              <>
                {currentTree.canViewTranscriptions && (
                  <>
                    <Button
                    onClick={() => {setIsOpenModalRegistrations(true)}}
                      className={classNames(
                        "genericButton",
                        "mr-1",
                        `${theme!.buttonColor} ${theme!.buttonBorderColor} ${
                          theme!.buttonTextColor
                        }`
                      )}
                    >
                      {t("btn_registrations", { ns: "cabolo" })}
                    </Button>
                  </>
                )}
                {currentTree.canDownloadZip && !isNativeApp && (
                  <DownloadZipButton
                    url={`/content/${sourceType}/${meetingId}/zip?languageId=${currentTree.languageId}`}
                  />
                )}
              
                <LanguageDropdown
                  languages={currentTree.availableLanguages}
                  selectedLanguage={currentTree.languageId}
                  className="py-0"
                  onLanguageSelect={(langId?: number) => {
                    if (langId) setSelectedLanguageId(langId);
                  }}
                  flagFontSize={25}
                />
              </>
            )}
          </div>
        </div>
      </Row>
      {isMobileMediaQuery && (
        <Row>
          {currentTree && (
            <div className="odgSottoTitoloMobile">
                {currentTree.canViewTranscriptions && !isNativeApp && (
                  <>
                    <Button
                    onClick={() => {setIsOpenModalRegistrations(true)}}
                      className={classNames(
                        "genericButton",
                        "mr-1",
                        `${theme!.buttonColor} ${theme!.buttonBorderColor} ${
                          theme!.buttonTextColor
                        }`
                      )}
                    >
                      {t("btn_registrations", { ns: "cabolo" })}
                    </Button>
                  </>
                )}
              {currentTree.canDownloadZip && !isNativeApp && (
                <DownloadZipButton
                  url={`/content/${sourceType}/${meetingId}/zip?languageId=${currentTree.languageId}`}
                />
              )}
              <div className="float-right">
                <LanguageDropdown
                  languages={currentTree.availableLanguages}
                  selectedLanguage={currentTree.languageId}
                  className="py-0"
                  onLanguageSelect={(langId?: number) => {
                    if (langId) setSelectedLanguageId(langId);
                  }}
                  flagFontSize={25}
                ></LanguageDropdown>
              </div>
            </div>
          )}
        </Row>
      )}
      <Row>
        <Col xs={12} className="odgContainer">
          {currentTree && (
            <TreeComponent
              tenant={currentTenant!}
              nodes={currentTree.tree}
              type={sourceType}
              sourceId={meetingId}
            />
          )}
        </Col>
      </Row>

      <GenericModal isOpen={isOpenModalRegistrations} 
      size="md"
      title={t("registrations", { ns: "cabolo" })}
      toggle={()=> setIsOpenModalRegistrations(false)}
      content={ 
        <>
        <CaboloRecordingMeetingComponent meetingId={meetingId}
         handleActionDownload={handleActionDownload}></CaboloRecordingMeetingComponent>
        </>
      }
      
      ></GenericModal>
    </div>
    </>
  );
};

export default observer(MeetingsTreePage);
